/*
 * Listing display rules shared by the public widgets and the dashboard.
 *
 * These answer "what does this listing qualify for", not "how do we draw it" —
 * so they belong here rather than in the widget's format helpers. The dashboard
 * needs the same answers to decide which inputs to offer: when the two sides
 * disagree, staff type a value into a field whose number never reaches a buyer.
 */
import type { TagColor } from './lookup.ts';

// Inventory type names that represent NCC Classic road vehicles rather than
// trailers. Drives (a) the classic-vehicles-only catalogue embed, (b) keeping
// cars out of the trailer catalogue, and (c) suppressing trailer-only UI such
// as financing estimates on their listings. Match is case-insensitive.
//
// "Custom Built" is the canonical classic-vehicle type — migration 0006 merges
// the historical duplicates ("Vehicle", "Vehicles", "Trucks") into it. The old
// names stay in this list so the split keeps working during the deploy window
// before that migration runs, and for any stragglers. Note: with this naming, a
// type literally called "Custom Built" is always treated as a vehicle — don't
// reuse that name for a custom *trailer* type.
export const VEHICLE_TYPE_NAMES = [
  'custom built',
  'vehicle',
  'vehicles',
  'truck',
  'trucks',
  'car',
  'cars',
  'suv',
  'suvs',
];

export function isVehicleType(typeName: string | null | undefined): boolean {
  if (typeName == null) return false;
  return VEHICLE_TYPE_NAMES.includes(typeName.trim().toLowerCase());
}

// Condition match is case-insensitive so the same helper works for the
// inventory enum ("New" | "Used") and the lowercase product one.
export function isUsedCondition(condition: string | null | undefined): boolean {
  if (condition == null) return false;
  return condition.trim().toLowerCase() === 'used';
}

// Whether a listing shows a "Used" condition badge. Buyers already know the NCC
// Classic vehicles are used, so the badge is noise on them — their per-listing
// display tag carries whatever wording we want instead. Trailers keep it, since
// there new and used sit side by side and the distinction matters.
export function showsUsedBadge(item: { type_name?: string | null }): boolean {
  return !isVehicleType(item.type_name);
}

// How long a newly posted listing carries the "Just Listed" badge.
export const JUST_LISTED_DAYS = 14;

export type ListingBadge = {
  kind: 'sold' | 'just-listed' | 'tag' | 'feature' | 'used';
  label: string;
  // Where on the photo the chip sits. NCC (2026-10-14): staff tags carry long
  // wording ("C/W Sliding Winch Bar with (8) 3-Bar Winches") that covered the
  // trailer, so they go along the bottom edge; the short status chips (Just
  // Listed, Sold, Used) stay top-left.
  placement: 'top' | 'bottom';
  // Tag and feature chips only: the colour staff picked for the tag. The other
  // kinds keep their fixed colours, so this is absent on them.
  color?: TagColor;
};

/**
 * The badges a listing shows, in display order, already decided.
 *
 * NCC's rule (2026-09-25): nothing promotional appears on its own. "Sale" and
 * "New" used to be added automatically from a sale price and the New condition,
 * which stacked a bare "Sale" beside the "Harvest Sale" tag staff had actually
 * chosen. Promo wording now comes only from the tag picked in the dashboard.
 * The one automatic badge is "Just Listed", for JUST_LISTED_DAYS after a
 * listing first goes live — published_at is stamped once and never moved, so
 * hiding and re-publishing does not restart the clock.
 *
 * "Used" stays automatic on trailers (never on classic vehicles, see
 * showsUsedBadge): it is a fact about the unit, not a promotion, and NCC didn't
 * ask for it to go. A sold listing shows Sold and nothing else.
 *
 * The tag comes through as kind 'tag' for a promotion and 'feature' for a tag
 * staff marked as describing the unit ("Comes with hydraulic pump"). Its colour
 * is picked per tag (2026-10-14) and sent as `color`; before that it followed
 * from the kind, which is why existing promos are yellow and features green.
 *
 * The public API sends this as `badges` so the site connector renders the list
 * rather than carrying its own copy of the rule — the same reason financing is
 * sent derived.
 */
export function listingBadges(
  item: {
    status?: string | null;
    condition?: string | null;
    type_name?: string | null;
    tag_name?: string | null;
    tag_style?: string | null;
    tag_color?: TagColor | null;
    published_at?: string | null;
    created_at?: string | null;
  },
  now: number = Date.now(),
): ListingBadge[] {
  if (item.status === 'sold') return [{ kind: 'sold', label: 'Sold', placement: 'top' }];
  const badges: ListingBadge[] = [];
  const listedAt = item.published_at ?? item.created_at;
  if (listedAt) {
    const ts = new Date(listedAt).getTime();
    if (ts >= now - JUST_LISTED_DAYS * 86_400_000) {
      badges.push({ kind: 'just-listed', label: 'Just Listed', placement: 'top' });
    }
  }
  if (item.tag_name) {
    const feature = item.tag_style === 'feature';
    badges.push({
      kind: feature ? 'feature' : 'tag',
      label: item.tag_name,
      placement: 'bottom',
      color: item.tag_color ?? (feature ? 'green' : 'yellow'),
    });
  }
  if (isUsedCondition(item.condition) && showsUsedBadge(item)) {
    badges.push({ kind: 'used', label: 'Used', placement: 'top' });
  }
  return badges;
}

/**
 * The listing's key features, for the detail page's price box. NCC
 * (2026-10-14) wanted the empty corner beside the price filled with whatever
 * the unit comes with — the wording of its feature tag ("C/W Wireless Remote").
 *
 * Only a tag staff marked as a Feature counts; a promotion ("Harvest Sale") is
 * an offer, not something the trailer comes with. Sent as a list because a
 * listing holds one tag today, and the price box should not have to change
 * shape if that grows. Empty on a sold listing, to match its badges.
 */
export function listingKeyFeatures(item: {
  status?: string | null;
  tag_name?: string | null;
  tag_style?: string | null;
}): string[] {
  if (item.status === 'sold') return [];
  return item.tag_name && item.tag_style === 'feature' ? [item.tag_name] : [];
}

/**
 * Whether the listing's type and condition allow financing at all. False for
 * every used unit, whatever its type (used trailers and used vehicles alike are
 * priced and financed case-by-case), and for NCC Classic vehicles in any
 * condition (we do not finance cars).
 *
 * This is the *rule*, and it is the floor — staff cannot switch financing on for
 * a listing this rejects. Callers deciding what to render, or which fields to
 * offer, want `isFinanced` instead: it also honours the per-listing off-switch.
 */
export function financingRuleAllows(item: {
  type_name?: string | null;
  condition?: string | null;
}): boolean {
  return !isVehicleType(item.type_name) && !isUsedCondition(item.condition);
}

/**
 * Whether a listing is financed — i.e. whether anything about payments belongs
 * on it. Two things must hold: the type/condition rule allows it, AND nobody has
 * turned financing off on this individual listing.
 *
 * One answer, two jobs: the widgets use it to suppress the bi-weekly figure, the
 * lease line, the OAC disclaimer and the financing CTA; the dashboard uses it to
 * hide the bi-weekly override field, since an amount typed there would never be
 * rendered. A manual `biweekly_payment` override does NOT override this — an
 * unfinanced listing shows no payment even with a number stored on it.
 *
 * `financing_disabled` only ever subtracts, by design. On a used unit or a
 * classic vehicle the rule already says no, so a stray `true` there changes
 * nothing and a `false` cannot resurrect financing. A checkbox must not be able
 * to publish a payment claim on inventory we've said we don't finance.
 *
 * Accepts number as well as boolean because SQLite hands back 0/1 for the column.
 */
export function isFinanced(item: {
  type_name?: string | null;
  condition?: string | null;
  financing_disabled?: boolean | number | null;
}): boolean {
  if (item.financing_disabled) return false;
  return financingRuleAllows(item);
}
